import { useMatomo } from '@datapunt/matomo-tracker-react';
import React, { useEffect } from 'react';

import Header from '@components/common/Header/Header';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import ArchiveFilter from './components/ArchiveFilter/ArchiveFilter';
import ArchiveTable from './components/ArchiveTable/ArchiveTable';
import type { IArchivedAiData, IArchivedAiStore } from './model/archivedAi';
import { useArchivedAiFilterDataQuery } from './services/useArchivedAiFilterData';
import { useArchivedAiListInfiniteQuery } from './services/useArchivedAiListInfiniteQuery';
import { selectArchiveFiltersData } from './store/useArchiveFiltersStore';
import style from './style.module.scss';

const Archive = () => {
  const { trackPageView } = useMatomo();

  const { t } = useTranslation();

  const locationSelectValue = selectAppLocationSelectValue();
  const {
    causeCategory,
    timeUpdatedRange,
    timeCreatedReportRange,
    costAvoidedRange,
    searchValue,
    type,
    sorting,
  }: IArchivedAiStore = selectArchiveFiltersData();
  const [debouncedSearchValue] = useDebouncedValue(searchValue, 500);
  const [debouncedCostAvoidedRange] = useDebouncedValue(costAvoidedRange, 500);

  const { data: filterData, isLoading: isLoadingFilterData } = useArchivedAiFilterDataQuery(
    locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  );
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useArchivedAiListInfiniteQuery({
    sorting,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    searchValue: debouncedSearchValue,
    causeCategory,
    timeUpdatedRange,
    timeCreatedReportRange,
    costAvoidedRange: debouncedCostAvoidedRange,
    type,
    enabled: true,
  });

  // @ts-ignore TODO: update react-query to v5
  const archiveAisData: IArchivedAiData = data;
  const archivedAiList = archiveAisData?.list || [];

  useEffect(() => {
    trackPageView({});
  }, []);

  return (
    <>
      <Header title={t('Archived Action Items')} showLogo={true} showFacilitySelect={true} isSecondaryPage />
      <div className={style.archivedAi}>
        <ArchiveFilter
          filterData={filterData}
          statData={archiveAisData?.stats}
          isLoading={isLoadingFilterData}
        />
        <ArchiveTable
          archivedAiList={archivedAiList}
          fetchNextPage={hasNextPage ? fetchNextPage : undefined}
          isFetching={isFetching}
          isLoading={isLoading}
          totalCount={archiveAisData?.totalCount}
        />
      </div>
    </>
  );
};

export default Archive;
